
import { computed, defineComponent, PropType, reactive, toRaw } from 'vue'

// components
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import ButtonDropdown from '@/common/components/UI/ButtonDropdown/index.vue'

// types
import { UserT } from '@/core/types/users.types'
import { users } from '@/core/realisations/users.realisation'
import useNotifications from '@/common/composable/useNotifications'

// composable
import useAccount from '@/common/composable/useAccount'

export default defineComponent({
    components: {
        'the-button': TheButton,
        cheap: Cheap,
        'button-dropdown': ButtonDropdown,
    },
    props: {
        item: {
            type: Object as PropType<UserT>,
            default: null,
        },
    },
    emits: ['update', 'delete', 'change-status', 'attach'],
    setup(props, { emit }) {
        const notifications = useNotifications()
        const { haveRole, havePermission } = useAccount()

        const state = reactive({
            is_user_session_loading: false,
            is_status_loading: false,
        })

        const emitUpdate = () => {
            emit('update', toRaw(props.item))
        }

        const emitDelete = () => {
            emit('delete', toRaw(props.item))
        }

        const createUserSession = async (url_target: string) => {
            try {
                state.is_user_session_loading = true
                const response = await users.getUserToken(props.item.id)

                let url = process.env.VUE_APP_IMPERSONATE_BPMS_URL as string

                if (url_target === 'gospark') {
                    url = process.env.VUE_APP_IMPERSONATE_URL as string
                } else if (url_target === 'pvz') {
                    url = process.env.VUE_APP_IMPERSONATE_PVZ_URL as string
                } else if (url_target === 'wms') {
                    url = process.env.VUE_APP_IMPERSONATE_WMS_URL as string
                }

                window.open(`${url}?impersonate_token=${response.access_token}`, '_blank')
            } catch (error) {
                console.error(error)
            } finally {
                state.is_user_session_loading = false
            }
        }

        const changeStatus = async (statusId: number) => {
            const answer = confirm(
                `Вы действительно хотите ${
                    statusId === 1 ? 'активировать' : 'деактивировать'
                } пользователя ${props.item.name}?`
            )
            if (!answer) return

            try {
                state.is_status_loading = true

                const payload = {
                    userId: props.item.id,
                    statusId,
                }
                await users.changeStatus(payload)

                notifications.notify({
                    title: `Пользователь ${statusId === 1 ? 'активирован' : 'деактивирован'}`,
                    type: 'success',
                })

                emit('change-status')
            } catch (error) {
                notifications.notifyError({
                    title: `При ${
                        statusId === 1 ? 'активации' : 'деактивации'
                    } пользователя произошла ошибка!`,
                    error,
                })
            } finally {
                state.is_status_loading = false
            }
        }

        const getRoles = computed(() => {
            if (props.item.roles && props.item.roles.length) {
                let result = ''

                props.item.roles.forEach((role) => {
                    result += result.length ? `, ${role.name}` : role.name
                })

                return result
            }

            return '-'
        })

        const emitAttach = () => {
            emit('attach', { ...props.item })
        }

        const isManager = computed(() => haveRole(20))

        const isGoSparkAuth = computed(
            () => havePermission('all-root-permission') || isManager.value
        )

        const formatDate = (dateStr: string) => {
            if (!dateStr) return '-'

            return dateStr.split('-').reverse().join('.')
        }

        return {
            state,
            emitUpdate,
            emitDelete,
            createUserSession,
            changeStatus,
            emitAttach,
            formatDate,
            //
            getRoles,
            isGoSparkAuth,
        }
    },
})
